import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Car, Video, FlaskConical, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const PARK_IMG = "/science-park.webp";

const PERKS = [
  { icon: FlaskConical, label: "Surrounded by science & tech innovators" },
  { icon: Car, label: "Easy reach from Bristol & Bath, just off the ring road" },
  { icon: Video, label: "In person at the park, or by video — your choice" },
];

export const LocationParallax = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section
      id="location"
      ref={ref}
      data-testid="location-section"
      className="relative py-32 sm:py-48 overflow-hidden"
    >
      <motion.div className="absolute inset-[-14%]" style={{ y }}>
        <img
          src={PARK_IMG}
          alt="The Forum building at Bristol & Bath Science Park"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-[#103080]/[0.82]" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#40C0E0] font-medium mb-6">
            <MapPin size={14} /> Where to find us
          </p>
          <h2 className="font-serif font-semibold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl mb-8">
            Proudly planted at <span className="italic text-[#40C0E0]">Bristol &amp; Bath Science Park.</span>
          </h2>
          <p className="text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl mb-12">
            Our home is one of the South West's leading innovation hubs — a place built
            for ambitious businesses. Pop in for a coffee and a chat about your numbers;
            we're easy to reach from both Bristol and Bath, with free parking on site.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mb-12">
          {PERKS.map((p, i) => (
            <Reveal key={p.label} delay={i * 0.1}>
              <div
                data-testid={`location-perk-${i}`}
                className="flex items-start gap-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/15 px-5 py-4"
              >
                <p.icon size={20} className="text-[#40C0E0] shrink-0 mt-0.5" strokeWidth={1.75} />
                <p className="text-sm text-white/85 leading-relaxed">{p.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <a
            href="https://www.google.com/maps/search/Bristol+%26+Bath+Science+Park,+Emersons+Green,+Bristol"
            target="_blank"
            rel="noreferrer"
            data-testid="location-directions-link"
            className="group inline-flex items-center gap-2 rounded-full bg-white text-[#103080] font-medium px-8 py-4 text-sm sm:text-base hover:bg-[#40C0E0] transition-colors duration-300"
          >
            Get directions to the Science Park
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </Reveal>
      </div>
    </section>
  );
};
